import { FeedbackSummaryActionType } from '@modules/oracle/feedbackSummaryStore/feedbackSummaryActionType';
import { useFeedbackSummaryStore } from '@modules/oracle/feedbackSummaryStore/feedbackSummaryStore';
import { FeedbackSummaryState } from '@modules/oracle/feedbackSummaryStore/types';
import { WebSocketMessage } from 'generated/graphql';
import { useCallback, useRef } from 'react';

interface captureSummaryMessageParams {
  webSocket: WebSocket;
}

interface UseCaptureSummaryMessage {
  responseID: string | undefined;
  sessionID: string | null;
}

export function useCaptureSummaryMessage({
  responseID,
  sessionID,
}: UseCaptureSummaryMessage) {
  const dispatch = useFeedbackSummaryStore(({ dispatch }) => dispatch);
  const userQuotesByExampleID = useFeedbackSummaryStore(
    ({ data }) => data.userQuotesByExampleID,
  );
  const userQuoteTokensByResponseID = useFeedbackSummaryStore(
    ({ data }) => data.userQuoteTokensByResponseID,
  );

  const messages = useFeedbackSummaryStore(({ data }) => data.messages);
  const shouldRejectIncomingMessages = useFeedbackSummaryStore(
    ({ visual }) => visual.shouldRejectIncomingMessages,
  );
  const sessionIDInStore = useFeedbackSummaryStore(
    ({ data }) => data.sessionID,
  );

  const deps = {
    userQuotesByExampleID,
    userQuoteTokensByResponseID,
    messages,
    responseID,
    shouldRejectIncomingMessages,
    sessionID,
    sessionIDInStore,
  };
  const depsRef = useRef(deps);
  depsRef.current = deps;

  const messageEventListener = useCallback(
    (event: MessageEvent) => {
      if (event.data) {
        const data: WebSocketMessage = JSON.parse(event.data);

        if (
          !data ||
          data.createSession ||
          depsRef.current.shouldRejectIncomingMessages ||
          depsRef.current.responseID !== data.responseID
        ) {
          return;
        }

        const responseIdFromResponse = data.responseID;

        if (data.endOfResponse) {
          dispatch({
            type: FeedbackSummaryActionType.SET_VISUAL,
            payload: {
              isCompleted: true,
            },
          });
        }

        const dataPayload: Partial<FeedbackSummaryState['data']> = {};

        if (!depsRef.current.sessionIDInStore) {
          dataPayload.sessionID = depsRef.current.sessionID;
        }

        const userQuote = data.feedbackSummary?.userQuote;
        if (userQuote) {
          dataPayload.userQuotesByExampleID = {
            ...depsRef.current.userQuotesByExampleID,
            [userQuote.exampleID]: {
              ...depsRef.current.userQuotesByExampleID[userQuote.exampleID],
              [userQuote.recordID]: userQuote,
            },
          };
        }

        const userQuoteToken = data.feedbackSummary?.userQuoteToken;
        if (userQuoteToken) {
          dataPayload.userQuoteTokensByResponseID = {
            ...depsRef.current.userQuoteTokensByResponseID,
            [responseIdFromResponse]: [
              ...(depsRef.current.userQuoteTokensByResponseID[
                responseIdFromResponse
              ] ?? []),
              userQuoteToken,
            ],
          };
        }

        if (data.feedbackSummary?.token) {
          dataPayload.messages = [...depsRef.current.messages, data];
        }

        if (Object.keys(dataPayload).length > 0) {
          dispatch({
            type: FeedbackSummaryActionType.SET_DATA,
            payload: dataPayload,
          });
        }

        if (data.error) {
          dispatch({
            type: FeedbackSummaryActionType.SET_VISUAL,
            payload: {
              error: data.error,
            },
          });
        }
      }
    },
    [dispatch],
  );

  const captureSummaryMessage = useCallback(
    ({ webSocket }: captureSummaryMessageParams) => {
      webSocket.removeEventListener('message', messageEventListener);
      webSocket.addEventListener('message', messageEventListener);
    },
    [messageEventListener],
  );

  return [captureSummaryMessage, { messages }] as const;
}
